import { useId, useState } from 'react';

import { FOCO_VISIVEL } from 'utils/foco';

import { agruparPorMes, contarEncerradas, Etapa, ItemDaLegenda, LEGENDA_DO_CALENDARIO } from '../calendario-do-ciclo';

export interface CalendarioDoCicloProps {
  /** `etapasDoCiclo(cycles)`; vazio esconde o bloco (calendário não carregou). */
  etapas: Etapa[];
  /** Horário do servidor, em milissegundos. */
  agora: number;
  /** Legenda das etapas; padrão: a da Realização Destaque. */
  legenda?: ItemDaLegenda[];
  /** Título do bloco; padrão: "Calendário do ciclo". */
  titulo?: string;
  /** Linha de apoio ao lado do título; padrão: a da Realização Destaque. */
  subtitulo?: string;
}

const MARCA = 'rounded-jj-pill px-[7px] py-0.5 text-[8.5px] font-bold uppercase tracking-[0.04em]';

/** Calendário do ciclo vigente: legenda, etapas agrupadas pelo mês de início e as encerradas sob demanda. */
export default function CalendarioDoCiclo({
  etapas,
  agora,
  legenda = LEGENDA_DO_CALENDARIO,
  titulo = 'Calendário do ciclo',
  subtitulo = 'Cadastramento, validação e apreciação das Realizações Destaque',
}: CalendarioDoCicloProps) {
  const [mostrarEncerradas, setMostrarEncerradas] = useState(false);
  const gradeId = useId();

  if (!etapas.length) return null;

  const encerradas = contarEncerradas(etapas, agora);
  const meses = agruparPorMes(etapas, agora, mostrarEncerradas, legenda);

  return (
    <section
      aria-labelledby="titulo-calendario"
      className="mt-[26px] rounded-jj-panel bg-brand-purple-light px-[26px] pb-6 pt-[22px]"
    >
      <div className="mb-1.5 flex flex-wrap items-baseline gap-2.5">
        <h2 id="titulo-calendario" className="m-0 font-display text-base font-bold text-brand-blue-dark">
          {titulo}
        </h2>
        {/* SPEC_DEVIATION: subtítulo em `content` e alternância em `brand-blue-dark` (o `text-soft` e o `brand-blue` do design ficam abaixo de 4,5:1 no lilás). */}
        <p className="m-0 text-[11.5px] text-content">{subtitulo}</p>
        {encerradas > 0 && (
          <button
            type="button"
            aria-expanded={mostrarEncerradas}
            aria-controls={gradeId}
            onClick={() => setMostrarEncerradas(atual => !atual)}
            className={`ml-auto cursor-pointer border-0 bg-transparent p-0 font-text text-[11px] font-bold uppercase tracking-[0.05em] text-brand-blue-dark hover:underline ${FOCO_VISIVEL}`}
          >
            {mostrarEncerradas ? 'Ocultar etapas encerradas' : `Ver etapas encerradas (${encerradas})`}
          </button>
        )}
      </div>

      <ul aria-label="Legenda" className="m-0 mb-4 flex list-none flex-wrap gap-3.5 p-0">
        {legenda.map(item => (
          <li key={item.tipo} className="inline-flex items-center gap-1.5 text-[10.5px] text-brand-navy">
            <span className={`h-2 w-2 rounded-full ${item.cor}`} aria-hidden="true" />
            {item.nome}
          </li>
        ))}
      </ul>

      <div id={gradeId} className="grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-3">
        {meses.map(mes => (
          <section
            key={mes.chave}
            aria-labelledby={`${gradeId}-${mes.chave}`}
            className="rounded-jj-lg bg-brand-white px-4 pb-2 pt-3.5"
          >
            <div className="flex items-baseline gap-2 border-b border-surface-200 pb-2.5">
              <h3 id={`${gradeId}-${mes.chave}`} className="m-0 font-display text-sm font-bold text-brand-blue-dark">
                {mes.nome} <span className="font-text text-[10px] font-normal text-content-soft">{mes.ano}</span>
              </h3>
              <span className="ml-auto text-[10px] text-content-soft">
                {mes.etapas.length} {mes.etapas.length === 1 ? 'etapa' : 'etapas'}
              </span>
            </div>
            <ul className="m-0 list-none p-0">
              {mes.etapas.map(etapa => (
                <li
                  key={`${etapa.tipo}-${etapa.inicio}`}
                  className="-mx-1.5 flex gap-[9px] rounded-jj-md border-b border-surface-100 px-1.5 py-2.5 last:border-b-0 hover:bg-surface-100"
                >
                  {/* SPEC_DEVIATION: a encerrada esmaece o ponto e passa o texto a `text-soft`; a opacidade 0,5 do design deixava o texto abaixo de 4,5:1. */}
                  <span
                    className={`mt-1 h-2 w-2 shrink-0 rounded-full ${etapa.cor} ${etapa.encerrada ? 'opacity-50' : ''}`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-[7px]">
                      <span
                        className={`font-display text-[11.5px] font-bold ${etapa.encerrada ? 'text-content-soft' : 'text-brand-blue-dark'}`}
                      >
                        {etapa.periodo}
                      </span>
                      {/* SPEC_DEVIATION: "Agora" em navy (branco sobre `brand-pink` dá 2,7:1). */}
                      {etapa.agora && <span className={`${MARCA} bg-brand-pink text-brand-navy`}>Agora</span>}
                      {etapa.encerrada && <span className={`${MARCA} bg-surface-200 text-content`}>Encerrada</span>}
                    </div>
                    <p
                      className={`m-0 mt-[3px] text-[11px] leading-[1.45] ${etapa.encerrada ? 'text-content-soft' : 'text-content'}`}
                    >
                      {etapa.nome} · <span className={etapa.encerrada ? '' : 'text-brand-blue'}>{etapa.referencia}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}
